import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { CareShell, Panel } from "../../components/care/CareLayout";
import { careService } from "../../services/careService";
import {
  downloadWeeklyReportPdf,
  weeklyReportEditorValuesFor,
  weeklyReportParagraphFor,
  weeklyReportSectionDefinitions,
  weeklyReportSectionsFor,
  weeklyReportTitleFor,
  weeklyReportWithEdits,
} from "../../utils/weeklyReportPdf";
import { formatDate } from "./AssessmentsPage";

const formatDateTime = (value) =>
  value ? new Date(value).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "Not set";

const reportKey = (report = {}) => report.id || `${report.weekStart}-${report.weekEnd}-${report.generatedAt}`;

export default function AssessmentWeeklyReportsPage() {
  const { assessmentId } = useParams();
  const [searchParams] = useSearchParams();
  const recordType = searchParams.get("recordType") || "resident-30-day-probation";
  const [reports, setReports] = useState([]);
  const [selectedReportId, setSelectedReportId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editingReportId, setEditingReportId] = useState("");
  const [reportEditValues, setReportEditValues] = useState(null);
  const [reportSaving, setReportSaving] = useState(false);
  const [reportSaveError, setReportSaveError] = useState("");

  const selectedReport = useMemo(
    () => selectedReportId ? reports.find((report) => reportKey(report) === selectedReportId) || null : null,
    [reports, selectedReportId],
  );

  const loadReports = useCallback(() => {
    if (!assessmentId) return;
    setLoading(true);
    setError("");
    careService.dailyNotes.listWeeklyReports({ assessmentProcessId: assessmentId, recordType })
      .then((data) => {
        const nextReports = Array.isArray(data) ? data : [];
        setReports(nextReports);
        setSelectedReportId((current) => current || reportKey(nextReports[0] || {}));
      })
      .catch((err) => {
        setReports([]);
        setSelectedReportId("");
        setError(err?.response?.data?.message || err?.message || "Unable to load generated weekly reports.");
      })
      .finally(() => setLoading(false));
  }, [assessmentId, recordType]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const downloadReport = (report = selectedReport) => {
    if (!report) return;
    downloadWeeklyReportPdf(report);
  };

  const startEditingReport = (report = selectedReport) => {
    if (!report) return;
    setEditingReportId(reportKey(report));
    setReportEditValues(weeklyReportEditorValuesFor(report));
    setReportSaveError("");
  };

  const cancelEditingReport = () => {
    setEditingReportId("");
    setReportEditValues(null);
    setReportSaveError("");
  };

  const updateReportSectionEdit = (sectionKey, value) => {
    setReportEditValues((current) => ({
      ...(current || {}),
      sections: {
        ...(current?.sections || {}),
        [sectionKey]: value,
      },
    }));
  };

  const saveReportEdits = async () => {
    if (!selectedReport || !reportEditValues || reportSaving) return;
    if (!selectedReport.id) {
      setReportSaveError("This report cannot be edited because it does not have a saved report id.");
      return;
    }
    setReportSaving(true);
    setReportSaveError("");
    try {
      const editedReport = weeklyReportWithEdits(selectedReport, reportEditValues);
      const savedReport = await careService.dailyNotes.updateWeeklyReport(
        selectedReport.id,
        { assessmentProcessId: assessmentId, recordType },
        editedReport,
      );
      const nextReport = savedReport || editedReport;
      setReports((current) => current.map((report) => (reportKey(report) === reportKey(selectedReport) ? nextReport : report)));
      setSelectedReportId(reportKey(nextReport));
      setEditingReportId("");
      setReportEditValues(null);
    } catch (err) {
      setReportSaveError(err?.response?.data?.message || err?.message || "Unable to save weekly report edits.");
    } finally {
      setReportSaving(false);
    }
  };

  const selectedReportIsEditing = selectedReport && editingReportId === reportKey(selectedReport);

  return (
    <CareShell className="dashboard-content residents-page assessments-page">
      <Panel
        title="Generated Weekly Reports"
        actionNode={(
          <div className="assessment-drawer-action-links">
            <button className="mini-btn" type="button" onClick={loadReports} disabled={loading}>
              <i className={`ti ${loading ? "ti-loader-2" : "ti-refresh"}`} /> Refresh
            </button>
            <Link className="mini-btn" to={`/assessments/${encodeURIComponent(assessmentId || "")}/new`}>
              <i className="ti ti-arrow-left" /> Back to assessment
            </Link>
          </div>
        )}
      >
        {error ? <p className="form-error">{error}</p> : null}
        {loading ? (
          <div className="weekly-report-generating" role="status">
            <i className="ti ti-loader-2" />
            <span>Loading generated weekly reports...</span>
          </div>
        ) : null}
        {!loading && !error && !reports.length ? (
          <p className="probation-empty">No weekly reports have been generated yet.</p>
        ) : null}
        {reports.length ? (
          <div className="weekly-reports-page-grid">
            <aside className="weekly-reports-page-list" aria-label="Generated reports">
              {reports.map((report) => {
                const key = reportKey(report);
                const active = key === reportKey(selectedReport || {});
                return (
                  <article className={`weekly-report-history-card ${active ? "active" : ""}`} key={key}>
                    <button type="button" onClick={() => setSelectedReportId(key)}>
                      <strong>{weeklyReportTitleFor(report)}</strong>
                      <span>{formatDate(report.weekStart)} - {formatDate(report.weekEnd)}</span>
                      <small>{report.noteCount || 0} notes reviewed{report.generatedAt ? ` - ${formatDateTime(report.generatedAt)}` : ""}</small>
                    </button>
                    <div className="weekly-report-card-actions">
                      <button className="mini-btn" type="button" onClick={() => setSelectedReportId(key)}>
                        <i className="ti ti-eye" /> View
                      </button>
                      <button className="mini-btn" type="button" onClick={() => {
                        setSelectedReportId(key);
                        startEditingReport(report);
                      }}>
                        <i className="ti ti-edit" /> Edit
                      </button>
                      <button className="mini-btn" type="button" onClick={() => downloadReport(report)}>
                        <i className="ti ti-download" /> PDF
                      </button>
                    </div>
                  </article>
                );
              })}
            </aside>
            {selectedReport ? (
              <section className="weekly-report-page-preview">
                <div className="assessment-section-heading-row">
                  <div>
                    <h3>{weeklyReportTitleFor(selectedReport)}</h3>
                    <span>Week covered: {formatDate(selectedReport.weekStart)} - {formatDate(selectedReport.weekEnd)}</span>
                  </div>
                  <div className="assessment-drawer-action-links">
                    <span>{selectedReport.noteCount || 0} notes reviewed</span>
                    {selectedReportIsEditing ? (
                      <>
                        <button className="mini-btn" type="button" onClick={saveReportEdits} disabled={reportSaving}>
                          <i className={`ti ${reportSaving ? "ti-loader-2" : "ti-device-floppy"}`} /> {reportSaving ? "Saving..." : "Save"}
                        </button>
                        <button className="mini-btn" type="button" onClick={cancelEditingReport} disabled={reportSaving}>
                          <i className="ti ti-x" /> Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button className="mini-btn" type="button" onClick={() => startEditingReport(selectedReport)}>
                          <i className="ti ti-edit" /> Edit
                        </button>
                        <button className="mini-btn" type="button" onClick={() => downloadReport(selectedReport)}>
                          <i className="ti ti-download" /> Download PDF
                        </button>
                        <button className="mini-btn" type="button" onClick={() => setSelectedReportId("")}>
                          <i className="ti ti-x" /> Close
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {selectedReport.generatedAt ? <small>Generated {formatDateTime(selectedReport.generatedAt)}</small> : null}
                {reportSaveError ? <p className="form-error">{reportSaveError}</p> : null}
                {selectedReportIsEditing ? (
                  <div className="weekly-report-editor">
                    <label className="ncp-form-field">
                      <span>Report title</span>
                      <input
                        type="text"
                        value={reportEditValues?.title || ""}
                        onChange={(event) => setReportEditValues((current) => ({ ...(current || {}), title: event.target.value }))}
                      />
                    </label>
                    <label className="ncp-form-field">
                      <span>Main paragraph</span>
                      <textarea
                        rows={5}
                        value={reportEditValues?.reportParagraph || ""}
                        onChange={(event) => setReportEditValues((current) => ({ ...(current || {}), reportParagraph: event.target.value }))}
                      />
                    </label>
                    <div className="weekly-report-editor-grid">
                      {weeklyReportSectionDefinitions.map(([title, sectionKey]) => (
                        <label className="ncp-form-field" key={sectionKey}>
                          <span>{title}</span>
                          <textarea
                            rows={5}
                            value={reportEditValues?.sections?.[sectionKey] || ""}
                            onChange={(event) => updateReportSectionEdit(sectionKey, event.target.value)}
                          />
                        </label>
                      ))}
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="weekly-report-paragraph">{weeklyReportParagraphFor(selectedReport)}</p>
                    {weeklyReportSectionsFor(selectedReport).length ? (
                      <div className="weekly-report-grid">
                        {weeklyReportSectionsFor(selectedReport).map(([title, items]) => (
                          <div className="weekly-report-section" key={title}>
                            <strong>{title}</strong>
                            <ul>
                              {items.map((item, index) => <li key={`${title}-${index}`}>{item}</li>)}
                            </ul>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </>
                )}
              </section>
            ) : (
              <section className="weekly-report-page-preview empty">
                <p className="probation-empty">Select a report to view its details.</p>
              </section>
            )}
          </div>
        ) : null}
      </Panel>
    </CareShell>
  );
}
